<template>
  <v-navigation-drawer
    v-model="panelOpen"
    location="right"
    temporary
    width="420"
    class="messages-side-panel"
    style="margin-top: 60px;"
    data-testid="messages-side-panel"
  >
    <div class="messages-panel-layout">
      <div class="messages-panel-header">
        <div class="d-flex align-center justify-space-between px-4 py-3">
          <div class="text-h6">{{ $t('messages.title') }}</div>
          <v-btn icon variant="text" size="small" @click="close" data-testid="messages-panel-close">
            <v-icon>fas fa-times</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>

        <div class="px-3 pt-3 pb-1">
          <v-text-field
            v-model="searchInput"
            density="compact"
            hide-details
            clearable
            variant="outlined"
            :placeholder="$t('messages.search_placeholder')"
            prepend-inner-icon="fas fa-search"
            data-testid="messages-search"
            @keyup.enter="() => applySearch({ force: true })"
            @click:clear="clearSearch"
          ></v-text-field>
          <div class="d-flex align-center justify-space-between mt-2">
            <v-checkbox
              v-model="regexInput"
              density="compact"
              hide-details
              :label="$t('messages.search_regex')"
              data-testid="messages-search-regex"
              @update:model-value="() => applySearch({ force: true })"
            ></v-checkbox>
            <v-btn
              size="small"
              variant="text"
              color="primary"
              data-testid="messages-search-submit"
              @click="() => applySearch({ force: true })"
            >
              {{ $t('messages.search') }}
            </v-btn>
          </div>
        </div>

        <div
          v-if="!loading && (pageCount > 1 || currentCount > 0)"
          class="d-flex flex-column align-center ga-1 px-3 pb-2"
          data-testid="messages-pagination"
        >
          <div class="text-caption text-grey" data-testid="messages-page-label">
            {{ pageLabel }}
          </div>
          <v-pagination
            v-if="pageCount > 1"
            :model-value="page"
            :length="pageCount"
            density="compact"
            total-visible="5"
            data-testid="messages-pagination-controls"
            @update:model-value="goToPage"
          ></v-pagination>
        </div>
        <v-divider v-if="!loading && (pageCount > 1 || currentCount > 0)"></v-divider>
      </div>

      <div class="messages-panel-body">
        <div v-if="loading" class="d-flex justify-center py-6">
          <v-progress-circular indeterminate size="32"></v-progress-circular>
        </div>

        <div v-else class="px-3 py-2">
          <div v-if="!items.length" class="text-body-2 text-grey py-4 px-1" data-testid="messages-empty">
            {{ searchActive ? $t('messages.no_results') : $t('messages.empty') }}
          </div>

          <v-card
            v-for="m in items"
            :key="'inbox-' + m.id"
            class="mb-3"
            variant="outlined"
            data-testid="messages-inbox-item"
          >
            <v-card-text class="pb-2">
              <div class="d-flex align-center justify-space-between mb-1">
                <div class="text-caption font-weight-medium">
                  <v-icon v-if="m.is_system" size="x-small" class="mr-1">fas fa-robot</v-icon>
                  <v-icon v-else size="x-small" class="mr-1">fas fa-user</v-icon>
                  {{ authorName(m) }}
                </div>
                <span class="text-caption text-grey">{{ formatDate(m.created_at) }}</span>
              </div>

              <div class="text-body-2 message-body" v-html="renderBody(m.body_markdown)"></div>

              <div
                v-if="m.asset"
                class="asset-preview mt-2 d-flex align-center"
                @click="openAsset(m.asset.id)"
              >
                <img
                  :src="thumbnailUrl(m.asset.static_path)"
                  class="asset-thumb mr-2"
                  alt=""
                />
                <span class="text-caption text-primary text-truncate">
                  {{ m.asset.filename }}
                </span>
              </div>
            </v-card-text>
            <v-card-actions class="pt-0">
              <v-btn
                v-if="m.asset"
                size="small"
                variant="text"
                color="primary"
                @click="openAsset(m.asset.id)"
              >
                {{ $t('messages.open_asset') }}
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                size="small"
                variant="text"
                @click="archive(m)"
              >
                {{ $t('messages.archive') }}
              </v-btn>
            </v-card-actions>
          </v-card>

          <template v-if="otherProjects.length">
            <v-divider class="my-3"></v-divider>
            <div class="text-caption font-weight-medium mb-2 px-1">
              {{ $t('messages.other_projects') }}
            </div>
            <v-list density="compact">
              <v-list-item
                v-for="p in otherProjects"
                :key="'other-project-' + p.project_id"
                @click="switchProject(p.project_id)"
              >
                <template v-slot:prepend>
                  <v-badge color="primary" :content="p.count" inline></v-badge>
                </template>
                <v-list-item-title class="text-body-2">
                  {{ p.project_name || ('#' + p.project_id) }}
                </v-list-item-title>
                <template v-slot:append>
                  <v-btn size="x-small" variant="text" color="primary">
                    {{ $t('messages.switch') }}
                  </v-btn>
                </template>
              </v-list-item>
            </v-list>
          </template>
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: 'MessagesSidePanel',
  data() {
    return {
      searchInput: '',
      regexInput: false,
      searchDebounce: null,
    };
  },
  computed: {
    panelOpen: {
      get() {
        return this.$store.getters['messages/is_panel_open'];
      },
      set(value) {
        this.$store.commit('messages/set_panel_open', value);
      },
    },
    items() {
      return this.$store.getters['messages/get_items'];
    },
    otherProjects() {
      return this.$store.getters['messages/other_projects'];
    },
    loading() {
      return this.$store.state.messages.loading;
    },
    page() {
      return this.$store.getters['messages/get_page'];
    },
    pageCount() {
      return this.$store.getters['messages/get_page_count'];
    },
    currentCount() {
      return this.$store.getters['messages/get_current_count'];
    },
    searchActive() {
      return !!(this.$store.getters['messages/get_search'] || '').trim();
    },
    pageLabel() {
      if (!this.currentCount) {
        return '';
      }
      return this.$t('messages.page_label', {
        page: this.page,
        pageCount: Math.max(this.pageCount, 1),
        count: this.currentCount,
      });
    },
  },
  watch: {
    panelOpen(open) {
      if (open) {
        this.searchInput = this.$store.getters['messages/get_search'] || '';
        this.regexInput = !!this.$store.getters['messages/get_regex'];
      }
    },
    searchInput() {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
      }
      this.searchDebounce = setTimeout(() => {
        this.applySearch();
      }, 350);
    },
  },
  beforeUnmount() {
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce);
    }
  },
  methods: {
    close() {
      this.$store.commit('messages/set_panel_open', false);
    },
    applySearch({ force = false } = {}) {
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      const search = (this.searchInput || '').trim();
      const regex = !!this.regexInput;
      const prevSearch = this.$store.getters['messages/get_search'] || '';
      const prevRegex = !!this.$store.getters['messages/get_regex'];
      if (!force && search === prevSearch && regex === prevRegex) {
        return;
      }
      this.$store.dispatch('messages/fetch_inbox', {
        search,
        regex,
        page: 1,
      });
    },
    clearSearch() {
      this.searchInput = '';
      this.regexInput = false;
      this.$store.dispatch('messages/fetch_inbox', {
        search: '',
        regex: false,
        page: 1,
      });
    },
    goToPage(page) {
      this.$store.dispatch('messages/fetch_inbox', { page });
    },
    authorName(m) {
      if (m.is_system) {
        return this.$t('messages.system');
      }
      if (m.author) {
        const name = [m.author.first_name, m.author.last_name]
          .filter(Boolean)
          .join(' ');
        return name || m.author.username || this.$t('messages.user');
      }
      return this.$t('messages.user');
    },
    thumbnailUrl(staticPath) {
      if (!staticPath) {
        return '';
      }
      const token = localStorage.token;
      const separator = staticPath.includes('?') ? '&' : '?';
      return this.$api_host + staticPath + separator + 'token=' + encodeURIComponent(token);
    },
    formatDate(value) {
      if (!value) {
        return '';
      }
      const d = new Date(value);
      if (isNaN(d.getTime())) {
        return value;
      }
      return d.toLocaleString();
    },
    renderBody(raw) {
      if (raw == null || raw === '') {
        return '';
      }
      const escaped = String(raw)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
      return escaped
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(
          /@([A-Za-z0-9_.+-]+)/g,
          '<span class="mention">@$1</span>',
        );
    },
    openAsset(assetId) {
      this.$store.dispatch('messages/open_asset', assetId);
    },
    async archive(m) {
      await this.$store.dispatch('messages/archive', {
        asset_id: m.asset_id,
        message_id: m.id,
      });
    },
    switchProject(projectId) {
      this.$store.commit('user/set_current_project_id', projectId);
      this.$store.dispatch('messages/fetch_inbox', {
        project_id: projectId,
        page: 1,
      });
      if (this.$route.name !== 'ProjectOverview') {
        this.$router.push({ name: 'ProjectOverview' });
      }
    },
  },
};
</script>

<style scoped>
.messages-panel-layout {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  min-height: 0;
}
.messages-panel-header {
  flex: 0 0 auto;
}
.messages-panel-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}
.message-body :deep(.mention) {
  color: #1976d2;
  font-weight: 600;
}
.asset-preview {
  cursor: pointer;
}
.asset-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}
</style>
